import SectionWrapper from '../SectionWrapper';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Text,
} from '@chakra-ui/react';
import FormImageUpload from './FormImageUpload';
import ColorPickerField from '../ColorPickerField';
import { useAppSelector } from '../../../../store/redux/hooks';
import { setFormField } from '../../../../store/redux/slices/form.slice';
import { useAppDispatch } from '../../../../store/redux/hooks';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import FieldEditIndicator from '../FieldEditIndicator';
import { useRef } from 'react';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';

const Design = () => {
  const form = useAppSelector((s) => s.form);
  const formEnabled = useAppSelector((s) => s.form.enabled);
  const dispatch = useAppDispatch();
  const { handleFocus, handleBlur, validateField } =
    useTemplateFieldValidation('form');
  const buttonTextRef = useRef<HTMLDivElement>(null);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: { buttonText: buttonTextRef },
  });
  const errors = useAppSelector((s) => s.templateErrors);

  const handleButtonTextChange = (value: string) => {
    dispatch(setFormField({ field: 'buttonText', value }));
    const updatedForm = { ...form, buttonText: value };
    validateField('buttonText', value, { form: updatedForm });
  };
  return (
    <SectionWrapper
      title="Form design"
      openOnLoad={true}
      id="form-design-section"
      subtitle={
        <Text>
          <Link
            color="linkBlue"
            href="https://docs.passentry.com/documentation/guides/custom-download-forms/guide#custom-pass-issuance-forms"
            target="_blank"
          >
            Need inspiration
          </Link>
          ? See how custom pass issuance forms are used.
        </Text>
      }
    >
      <Flex direction="column" gap={4}>
        <Flex direction="row" gap={4}>
          <FormImageUpload
            label="Header image"
            field="formLogoImage"
            recommendation="Recommended size: 360x110 pixels. Recommended file type is PNG with transparent background."
            aspectRatio={360 / 110}
            isDisabled={!formEnabled}
          />
          <FormImageUpload
            label="Favicon"
            field="formFaviconImage"
            recommendation="Recommended size: 16x16 pixels. Recommended file type is PNG with transparent background."
            aspectRatio={1}
            isDisabled={!formEnabled}
          />
        </Flex>
        <FormLabel
          variant="templateBuilderLabel"
          mt={2}
          fontSize="18px"
          fontWeight="500"
        >
          Colours
        </FormLabel>
        <Flex direction="row" gap={4}>
          <ColorPickerField
            label="Background"
            color={form.backgroundColor}
            field="backgroundColor"
            namespace="form"
            isDisabled={!formEnabled}
          />
          <ColorPickerField
            label="Component"
            color={form.componentFillColor}
            field="componentFillColor"
            namespace="form"
            isDisabled={!formEnabled}
          />
        </Flex>
        <ColorPickerField
          label="Font"
          color={form.componentFontColor}
          field="componentFontColor"
          namespace="form"
          isDisabled={!formEnabled}
        />
        <FormLabel
          variant="templateBuilderLabel"
          mt={2}
          fontSize="18px"
          fontWeight="500"
        >
          Button colours
        </FormLabel>
        <Flex direction="row" gap={4}>
          <ColorPickerField
            label="Background"
            color={form.buttonColor}
            field="buttonColor"
            namespace="form"
            isDisabled={!formEnabled}
          />
          <ColorPickerField
            label="Font"
            color={form.buttonCtaColor}
            field="buttonCtaColor"
            namespace="form"
            isDisabled={!formEnabled}
          />
        </Flex>
        <FieldEditIndicator
          fieldRef={buttonTextRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="55px"
        >
          <FormControl isInvalid={!!errors['form.buttonText']} isRequired>
            <FormLabel variant="templateBuilderLabel">Button text</FormLabel>
            <Input
              value={form.buttonText}
              onChange={(e) => handleButtonTextChange(e.target.value)}
              onFocus={() => handleFocus('buttonText')}
              onBlur={handleBlur}
              isDisabled={!formEnabled}
            />
            <FormErrorMessage
              pl={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {errors['form.buttonText']}
            </FormErrorMessage>
          </FormControl>
        </FieldEditIndicator>
      </Flex>
    </SectionWrapper>
  );
};

export default Design;
